/**
 * ============================================================================
 * ViewSelector — Phase 6b frontend (Quality Metrics Dashboard v2)
 * Owner: Claude 3 (Frontend & UX).
 * Purpose: segmented control at the top-right of the dashboard. Lets the user
 *          switch between QA Director / Site Quality Head / Department Manager
 *          / Inspector views — server returns a different metric mix per view.
 *
 * A11Y: implemented as a true radiogroup (button role="radio") with arrow-key
 *  navigation, so it scans correctly to screen readers as "View, 1 of 4
 *  selected".
 * ============================================================================ */

import { useRef } from 'react';
import { useTranslation } from 'react-i18next';
import styles from './QualityMetricsDashboard.module.css';
import type { DashboardView } from './useDashboardQuery';

export interface ViewSelectorProps {
  value: DashboardView;
  onChange: (view: DashboardView) => void;
}

const VIEWS: DashboardView[] = [
  'QA_DIRECTOR',
  'SITE_QUALITY_HEAD',
  'DEPARTMENT_MANAGER',
  'INSPECTOR',
];

export function ViewSelector({ value, onChange }: ViewSelectorProps) {
  const { t } = useTranslation();
  const refs = useRef<Array<HTMLButtonElement | null>>([]);

  function focusIndex(i: number) {
    const next = (i + VIEWS.length) % VIEWS.length;
    refs.current[next]?.focus();
    onChange(VIEWS[next]!);
  }

  function onKeyDown(e: React.KeyboardEvent<HTMLButtonElement>, idx: number) {
    if (e.key === 'ArrowRight' || e.key === 'ArrowDown') {
      e.preventDefault();
      focusIndex(idx + 1);
    } else if (e.key === 'ArrowLeft' || e.key === 'ArrowUp') {
      e.preventDefault();
      focusIndex(idx - 1);
    } else if (e.key === 'Home') {
      e.preventDefault();
      focusIndex(0);
    } else if (e.key === 'End') {
      e.preventDefault();
      focusIndex(VIEWS.length - 1);
    }
  }

  return (
    <div
      className={styles.viewSelector}
      role="radiogroup"
      aria-label={t('qualityMetrics.viewSelector.aria', {
        defaultValue: 'Dashboard view',
      })}
    >
      {VIEWS.map((v, idx) => {
        const active = v === value;
        return (
          <button
            key={v}
            ref={(el) => {
              refs.current[idx] = el;
            }}
            type="button"
            role="radio"
            aria-checked={active}
            tabIndex={active ? 0 : -1}
            className={`${styles.viewSelectorButton} ${active ? styles.viewSelectorButtonActive : ''}`}
            onClick={() => onChange(v)}
            onKeyDown={(e) => onKeyDown(e, idx)}
          >
            {t(`qualityMetrics.viewSelector.${v}`, {
              defaultValue: v.replace(/_/g, ' '),
            })}
          </button>
        );
      })}
    </div>
  );
}
